import { Link } from 'react-router-dom';
import { useT } from '../../hooks/useT';

const GOAL_TYPE_EMOJIS = {
  car: '🚗', wedding: '💍', travel: '✈️', electronics: '🎮',
  emergency: '🛡️', real_estate: '🏠', education: '📚', custom: '⭐',
};

const STATUS_STYLES = {
  active:    'bg-emerald-900/40 text-emerald-400',
  paused:    'bg-yellow-900/40 text-yellow-400',
  completed: 'bg-brand-900/40 text-brand-400',
  archived:  'bg-slate-700/40 text-slate-400',
};

function fmt(n, currency = 'USD') {
  const locale = currency === 'BRL' ? 'pt-BR' : currency === 'EUR' ? 'de-DE' : 'en-US';
  return parseFloat(n || 0).toLocaleString(locale, { style: 'currency', currency });
}

export default function GoalCard({ goal, onGeneratePlan, onComplete, activePlan, paid, isGenerating, currency = 'USD' }) {
  const t = useT();
  const emoji = GOAL_TYPE_EMOJIS[goal.type] || GOAL_TYPE_EMOJIS.custom;
  const typeLabel = t(`goal.type.${goal.type}`) || t('goal.type.custom');
  const cost = goal.target_amount || goal.ai_estimated_cost;

  // Money progress
  const savingsPct = parseFloat(activePlan?.progress_percent || 0);
  const savedAmount = parseFloat(activePlan?.cumulative_savings || 0);
  const isMoneyComplete = savingsPct >= 100;

  // Time progress
  const estimatedMonths = parseInt(activePlan?.estimated_months || 0, 10);
  const monthsElapsed = activePlan
    ? Math.floor((Date.now() - new Date(activePlan.created_at)) / (1000 * 60 * 60 * 24 * 30.4375))
    : 0;
  const timePct = estimatedMonths > 0 ? Math.min(100, (monthsElapsed / estimatedMonths) * 100) : 0;
  const isTimeComplete = estimatedMonths > 0 && monthsElapsed >= estimatedMonths;

  const isComplete = goal.status === 'completed';
  const isFull = isMoneyComplete || isTimeComplete;

  return (
    <div className={`card group hover:border-slate-600 transition-colors animate-fade-in ${isFull && !isComplete ? 'border-green-600/50' : ''}`}>
      {/* Header */}
      <div className="flex items-start justify-between gap-4 mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-surface-hover rounded-xl flex items-center justify-center text-xl">
            {emoji}
          </div>
          <div>
            <h3 className="font-semibold text-slate-100">{goal.title}</h3>
            <p className="text-xs text-slate-500">{typeLabel}</p>
          </div>
        </div>
        <span className={`text-xs font-medium px-2.5 py-0.5 rounded-full ${STATUS_STYLES[goal.status] || STATUS_STYLES.active}`}>
          {t(`goal.status.${goal.status}`) || goal.status}
        </span>
      </div>

      {/* Congratulations banner */}
      {isFull && !isComplete && (
        <div className="mb-4 rounded-xl bg-green-900/30 border border-green-700/50 px-4 py-3 flex items-center gap-2">
          <span className="text-xl">🎉</span>
          <div>
            <p className="text-green-300 font-semibold text-sm">{t('goal.card.congrats.title')}</p>
            <p className="text-green-400/70 text-xs mt-0.5">
              {isMoneyComplete && isTimeComplete && t('goal.card.congrats.both')}
              {isMoneyComplete && !isTimeComplete && t('goal.card.congrats.money')}
              {!isMoneyComplete && isTimeComplete && t('goal.card.congrats.time')}
              {' '}{t('goal.card.congrats.cta')}
            </p>
          </div>
        </div>
      )}

      {/* Completed banner */}
      {isComplete && (
        <div className="mb-4 rounded-xl bg-brand-900/20 border border-brand-800/40 px-4 py-3 flex items-center gap-2">
          <span className="text-xl">🏆</span>
          <p className="text-brand-300 font-semibold text-sm">{t('goal.card.completed.badge')}</p>
        </div>
      )}

      {/* Cost */}
      {cost && (
        <div className="mb-4">
          <p className="text-xs text-slate-500 mb-0.5">{t('goal.card.target_value')}</p>
          <p className="text-2xl font-bold text-slate-100">
            {fmt(cost, currency)}
          </p>
          {goal.ai_estimated_cost && !goal.target_amount && (
            <p className="text-xs text-slate-500 mt-0.5">{t('goal.card.ai_estimated')}</p>
          )}
        </div>
      )}

      {/* Deadline */}
      {goal.deadline && (
        <div className="mb-4 flex items-center gap-1.5 text-sm text-slate-400">
          <span>📅</span>
          <span>{t('goal.card.deadline').replace('{date}', new Date(goal.deadline).toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' }))}</span>
        </div>
      )}

      {/* Active plan progress */}
      {activePlan && (
        <div className="mb-4 p-3 bg-surface-hover rounded-xl space-y-3">
          {/* Money saved */}
          <div>
            <div className="flex justify-between text-xs mb-1">
              <span className="text-slate-400">{t('goal.card.progress.saved')}</span>
              <span className={isMoneyComplete ? 'text-green-400 font-semibold' : 'text-slate-400'}>
                {fmt(savedAmount, currency)} / {cost ? fmt(cost, currency) : '—'}
                {isMoneyComplete && ' ✓'}
              </span>
            </div>
            <div className="w-full h-1.5 bg-surface rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full transition-all duration-500 ${isMoneyComplete ? 'bg-green-500' : 'bg-brand-500'}`}
                style={{ width: `${Math.min(100, savingsPct)}%` }}
              />
            </div>
          </div>

          {/* Time elapsed */}
          <div>
            <div className="flex justify-between text-xs mb-1">
              <span className="text-slate-400">{t('goal.card.progress.time')}</span>
              <span className={isTimeComplete ? 'text-green-400 font-semibold' : 'text-slate-400'}>
                {t('goal.card.progress.months').replace('{elapsed}', monthsElapsed).replace('{total}', estimatedMonths)}
                {isTimeComplete && ' ✓'}
              </span>
            </div>
            <div className="w-full h-1.5 bg-surface rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full transition-all duration-500 ${isTimeComplete ? 'bg-green-500' : 'bg-amber-500'}`}
                style={{ width: `${timePct}%` }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center gap-2 pt-2 border-t border-surface-border">
        {!activePlan && goal.status === 'active' && (
          <button
            onClick={() => !isGenerating && onGeneratePlan(goal)}
            disabled={isGenerating}
            className={`flex-1 btn-primary text-sm py-2 ${isGenerating ? 'opacity-60 cursor-not-allowed' : ''}`}
          >
            {isGenerating ? (
              <span className="flex items-center gap-2 justify-center">
                <span className="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                {t('goal.card.generating')}
              </span>
            ) : t('goal.card.generate')}
          </button>
        )}
        {!activePlan && isComplete && (
          <span className="flex-1 text-center text-xs text-slate-600 py-2 cursor-default" title={t('goal.card.completed_no_plans.tooltip')}>
            {t('goal.card.completed_no_plans')}
          </span>
        )}
        {activePlan && (
          <Link to={`/plans/${activePlan.id}`} className="text-xs btn-ghost">
            {t('goal.card.view_plan')}
          </Link>
        )}
        {goal.status === 'active' && (
          <button
            onClick={() => onComplete(goal)}
            className={`btn-ghost text-sm ${isFull ? 'text-green-400 font-semibold' : ''}`}
          >
            {t('goal.card.done')}
          </button>
        )}
      </div>
    </div>
  );
}

